import type { BandSpace, Persisted, Profile, Rider, RiderMember } from '@tunasong/schemas'
import { getProfileGear } from './profile-gear.js'
import type { BandPartsInfo } from './stage-setup.js'
import { snapshotSetup } from './stage-setup.js'

/**
 * Generate a rider from bandspace member profiles. Members who don't perform with the band are left off.
 * Without `bandParts` (the request failed) everyone is included and roles fall back to the profile's free text.
 */
export function generateRiderFromProfiles(
  bandspace: Persisted<BandSpace>,
  memberProfiles: Profile[],
  bandParts?: BandPartsInfo
): Partial<Rider> {
  const performers = memberProfiles.filter(profile => !bandParts?.notPerforming.includes(profile.userId))

  const members: RiderMember[] = performers.map((profile, index) => ({
    userId: profile.userId,
    ...snapshotSetup(getProfileGear(profile), bandParts?.parts[profile.userId]),
    channelNumber: index + 1,
    monitorMix: index + 1,
  }))

  // Aggregate monitor needs from member preferences
  const monitorNeeds = performers.reduce(
    (acc, profile) => {
      const gear = getProfileGear(profile)
      const pref = gear?.monitorPreference
      if (pref === 'wedge' || pref === 'both') {
        acc.wedges = (acc.wedges ?? 0) + 1
      }
      if (pref === 'iem' || pref === 'both') {
        acc.iemPacks = (acc.iemPacks ?? 0) + 1
      }
      return acc
    },
    { wedges: 0, iemPacks: 0, mixes: performers.length }
  )

  return {
    type: 'rider',
    name: `${bandspace.name ?? 'Band'} Technical Rider`,
    parentId: bandspace.id,
    members,
    shared: {
      monitors: monitorNeeds,
    },
    generatedFromProfiles: true,
  }
}
